import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import {
  type SupplierCustomValue,
  type DailyRevenueSupplierExpense,
  type SupplierDefaultValue,
  type MonthlyGenerationStatusMap,
} from "../../../utils/api/supplierExpensesAPI";
import { useGetFinanceOverviewQuery } from "../../../utils/redux/api/financeApi";

export interface Category {
  _id: string;
  name: string;
  expenseType?: "direct" | "operating";
  isFrozen?: boolean;
  suppliers: Array<{
    _id: string;
    name: string;
  }>;
}

export interface Location {
  _id: string;
  name: string;
  clientId: string;
}

export const useFinanceData = (clientId: string, months: string[]) => {
  const { t } = useTranslation();
  const workLocationsState = useSelector((state: any) => state.workLocations);

  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  // Get locations from Redux first, fallback to API if needed
  const cachedLocations = clientId
    ? workLocationsState?.clientLocations?.[clientId]
    : null;

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/admin/expense-categories`,
        { params: { clientId, showFrozen: "true" } },
      );
      setCategories(data?.categories || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      message.error(t("Failed to load categories"));
    }
  }, [clientId, t]);

  // Fetch locations (use Redux cache first)
  const fetchLocations = useCallback(async () => {
    if (!clientId) return;

    // Use cached locations if available
    if (cachedLocations && cachedLocations.length > 0) {
      setLocations(cachedLocations);
      return;
    }

    // Otherwise fetch from API
    try {
      const { data } = await axios.get(`${BASE_API_URL}/${clientId}/locations`);
      setLocations(data || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
      message.error(t("Failed to load locations"));
    }
  }, [clientId, cachedLocations, t]);

  // Custom/default/daily-revenue/generation-status data — these 4 are
  // always requested together, so they're fetched as one combined request
  // (cached by RTK Query, shared & deduped across any component requesting
  // the same clientId+months) instead of 4 separate round trips.
  const monthsKey = months.join(",");
  const skip = !clientId || !months || months.length === 0;

  const overviewQuery = useGetFinanceOverviewQuery(
    { clientId, months: monthsKey },
    { skip },
  );

  // Surface fetch errors the same way the old axios callbacks did
  useEffect(() => {
    if (overviewQuery.isError) message.error(t("Failed to load custom values"));
  }, [overviewQuery.isError, t]);

  const defaultValues: SupplierDefaultValue[] = overviewQuery.data?.defaultValues || [];
  const customValues: SupplierCustomValue[] = overviewQuery.data?.customValues || [];
  const dailyRevenueExpenses: DailyRevenueSupplierExpense[] =
    overviewQuery.data?.dailyRevenueExpenses || [];
  const generationStatus: MonthlyGenerationStatusMap =
    overviewQuery.data?.generationStatus || {};

  // isLoading (not isFetching): true only for the first-ever fetch with no
  // cached data yet, so a background revalidation on an already-visited
  // page doesn't flip this back to true and unmount the gated children below.
  const loading = overviewQuery.isLoading;

  // All 4 datasets come from the same combined request, so refetching any
  // one of them refreshes all 4 together.
  const refetchOverview = useCallback(async () => {
    await overviewQuery.refetch();
  }, [overviewQuery]);
  const refetchCustomValues = refetchOverview;
  const refetchDailyRevenueExpenses = refetchOverview;
  const refetchGenerationStatus = refetchOverview;

  // Load categories and locations on mount
  useEffect(() => {
    if (!clientId) return;
    fetchCategories();
    fetchLocations();
  }, [clientId, fetchCategories, fetchLocations]);

  return {
    categories,
    locations,
    defaultValues,
    customValues,
    dailyRevenueExpenses,
    generationStatus,
    loading,
    refetchCustomValues,
    refetchDailyRevenueExpenses,
    refetchGenerationStatus,
  };
};
